// URL-only state for the /transactions browser. Every navigable view is
// reachable by URL alone (ui-spec §7.2), so the parsers must be total — they
// accept partial / missing / malformed input and resolve to defaults rather
// than throwing.

// Deep import — `@observatory/shared`'s barrel re-exports node-only modules
// (cli-session, ingest adapters, etc.) which break the browser bundle when
// pulled in transitively. The db-types subpath is browser-safe: it is a pure
// generated TS module with zero runtime side-effects.
import { Constants as DbConstants } from '@observatory/shared/db-types.ts';
// Same for the period model: a pure module, shared with the Edge Functions
// and the accountant MCP so every surface reads one period grammar.
import {
  type CalendarPeriod,
  isCalendarDate,
  PeriodError,
  type PeriodQuery,
  parsePeriodQuery,
  parsePeriodString,
} from '@observatory/shared/period.ts';

// Resolution filter — the union of three synthetic sentinels and every
// `transaction_link_type` enum value. The enum drives the link-type half so
// adding a new value (e.g. `bank_reversal` from #104) lights up the filter
// dropdown without any UI edit.
//
// Synthetic sentinels:
//  - `any`        — no resolution predicate
//  - `attached`   — has at least one document_assignment
//  - `unresolved` — no document AND no resolution link (the matcher's queue)
const RESOLUTION_SYNTHETIC = ['any', 'attached', 'unresolved'] as const;
export const RESOLUTION_FILTERS = [
  ...RESOLUTION_SYNTHETIC,
  ...DbConstants.public.Enums.transaction_link_type,
] as const;
export type ResolutionFilter = (typeof RESOLUTION_FILTERS)[number];

// `current-month` resolves at render time (deep link "no period" = this
// month); every other kind is a CalendarPeriod: month (YYYY-MM), quarter
// (YYYY-Qn), year (YYYY) or range (YYYY-MM-DD, inclusive). The range is
// not checked for order or length here, so a parser stays total; the export
// surfaces refuse what they cannot cover via periodBounds.
export type Period = { kind: 'current-month' } | CalendarPeriod;

export type AccountScope = { kind: 'all' } | { kind: 'one'; slug: string };

// Mirrors the canonical `categorization_status` enum (see CONTEXT.md). v1's
// dropdown only surfaces `uncategorized` / `auto` / `manual`, but the URL
// schema must round-trip the full enum so a deep link to
// `?status=agent_proposed` doesn't silently collapse to `any` and mislead the
// user — the value is forwarded straight through to the query layer.
export type CategorizationStatusFilter =
  | 'any'
  | 'uncategorized'
  | 'auto'
  | 'agent_proposed'
  | 'manual';

// `amount` filter — narrows the list by the sign of `amount_eur`. `expenses`
// is the same predicate the MCP `list_spendings` tool uses as its default
// queue (amount_eur < 0); strict MCP-parity (exclude internal-transfer
// linked rows + already-assigned) lands when a server-side view backs the
// query — for now the UI definition is "negative amounts".
export const AMOUNT_FILTERS = ['any', 'expenses', 'negative', 'positive'] as const;
export type AmountFilter = (typeof AMOUNT_FILTERS)[number];

// Legacy alias kept so the symbol export is still resolvable while consumers
// migrate to `RESOLUTION_FILTERS` / `ResolutionFilter` — the resolution filter
// supersedes the old "any / attached / unattached" Document filter (see #106).
// Will be deleted once every callsite has been updated.
export const DOCUMENT_FILTERS = ['any', 'attached', 'unattached'] as const;
export type DocumentFilter = (typeof DOCUMENT_FILTERS)[number];

export const SORT_COLUMNS = [
  'booked_at',
  'amount_eur',
  'counterparty_raw_name',
  'category_id',
] as const;
export type SortColumn = (typeof SORT_COLUMNS)[number];
export type SortDirection = 'asc' | 'desc';
export type Sort = { column: SortColumn; direction: SortDirection };

export interface TransactionsSearch {
  period: Period;
  /** Set when the link's period could not be read; see LinkPeriod. */
  invalidPeriod: InvalidPeriod | null;
  account: AccountScope;
  status: CategorizationStatusFilter;
  categoryId: string | null;
  counterpartyId: string | null;
  amountMin: number | null;
  amountMax: number | null;
  q: string | null;
  sort: Sort;
  selected: string | null;
  focus: string | null;
  noanim: boolean;
  amountFilter: AmountFilter;
  resolution: ResolutionFilter;
}

// SearchParams allows both string scalars (canonical URL form like
// `?period=2026-05`) and object/array values that TanStack Router's default
// JSON search-serializer round-trips back through validateSearch (e.g.
// `?period={"kind":"month","month":"2026-05"}`). Parsers below tolerate
// both shapes so the URL survives a navigate → URL → re-parse cycle.
export type SearchParams = Record<string, unknown>;

function readString(params: SearchParams, key: string): string | undefined {
  const v = params[key];
  if (typeof v === 'string' && v.length > 0) return v;
  return undefined;
}

function readObject(params: SearchParams, key: string): Record<string, unknown> | null {
  const v = params[key];
  if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
    return v as Record<string, unknown>;
  }
  return null;
}

// A period string of the given kind, or null.
function periodOfKind<K extends CalendarPeriod['kind']>(
  value: unknown,
  kind: K,
): Extract<CalendarPeriod, { kind: K }> | null {
  if (typeof value !== 'string') return null;
  const parsed = parsePeriodString(value);
  return parsed?.kind === kind ? (parsed as Extract<CalendarPeriod, { kind: K }>) : null;
}

export function parsePeriod(params: SearchParams): Period {
  // String form (`?period=2026-05`, `2026-Q2`, `2026`) — the canonical URL
  // shape. TanStack Router's default parser JSON-decodes `?period=2026`
  // into the number 2026, so an integer counts as its digits.
  const raw = params.period;
  const period = typeof raw === 'string' ? raw : Number.isInteger(raw) ? String(raw) : undefined;
  const fromPeriodParam = period ? parsePeriodString(period) : null;
  if (fromPeriodParam) return fromPeriodParam;

  // Object form (`?period={"kind":"month","month":"2026-05"}`) — produced
  // when TanStack Router's default JSON serializer round-trips the already-
  // validated Period back into the URL. Accepting it here makes navigation
  // idempotent and stops the picker from snapping back to current-month
  // after every URL update.
  const periodObj = readObject(params, 'period');
  if (periodObj) {
    const month = periodObj.kind === 'month' ? periodOfKind(periodObj.month, 'month') : null;
    if (month) return month;
    const quarter =
      periodObj.kind === 'quarter' ? periodOfKind(periodObj.quarter, 'quarter') : null;
    if (quarter) return quarter;
    const year = periodObj.kind === 'year' ? periodOfKind(periodObj.year, 'year') : null;
    if (year) return year;
    if (periodObj.kind === 'current-month') return { kind: 'current-month' };
    if (
      periodObj.kind === 'range' &&
      typeof periodObj.from === 'string' &&
      typeof periodObj.to === 'string' &&
      isCalendarDate(periodObj.from) &&
      isCalendarDate(periodObj.to)
    ) {
      return { kind: 'range', from: periodObj.from, to: periodObj.to };
    }
  }

  const from = readString(params, 'from');
  const to = readString(params, 'to');
  if (from && to && isCalendarDate(from) && isCalendarDate(to)) {
    return { kind: 'range', from, to };
  }
  return { kind: 'current-month' };
}

export function serializePeriod(value: Period): SearchParams {
  if (value.kind === 'month') return { period: value.month };
  if (value.kind === 'quarter') return { period: value.quarter };
  // As a number, TanStack Router writes a clean `?period=2026`; a digit
  // string would be JSON-quoted (`?period=%222026%22`) so it parses back as
  // a string. parsePeriod reads both.
  if (value.kind === 'year') {
    return { period: String(Number(value.year)) === value.year ? Number(value.year) : value.year };
  }
  if (value.kind === 'range') return { from: value.from, to: value.to };
  return {};
}

/**
 * A link whose period could not be read: why, in the words of the shared
 * request parser, and the period params it carried (`period`, `from`, `to`,
 * as given), which stay in the URL until a period is picked.
 */
export interface InvalidPeriod {
  readonly reason: string;
  readonly query: Readonly<Record<string, unknown>>;
}

/**
 * The period a link asks for. An unreadable one is never silently another
 * period: `period` is then the current month and `invalidPeriod` says why,
 * for the page to show.
 */
export interface LinkPeriod {
  readonly period: Period;
  readonly invalidPeriod: InvalidPeriod | null;
}

const PERIOD_KEYS = ['period', 'from', 'to'] as const;

function periodParams(input: SearchParams): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  for (const key of PERIOD_KEYS) {
    if (input[key] !== undefined) params[key] = input[key];
  }
  return params;
}

// The router merges the parsed state over the URL params before the URL is
// written (see apps/web/src/router.tsx), so an unreadable link's `period`
// arrives here as the parsed current month with the link's own params
// under `invalidPeriod.query`. Put those back so they are read again.
function withLinkPeriodParams(input: SearchParams): SearchParams {
  const invalid = readObject(input, 'invalidPeriod');
  const query = invalid ? readObject(invalid, 'query') : null;
  if (readObject(input, 'period')?.kind !== 'current-month' || query === null) return input;
  const rest = { ...input };
  for (const key of PERIOD_KEYS) delete rest[key];
  return { ...rest, ...periodParams(query) };
}

// Absent and empty mean "no value"; anything else, JSON null included (the
// router decodes `?period=null` to it), is a value to read or refuse.
function asQueryValue(value: unknown): string | null {
  if (value === undefined || value === '') return null;
  if (value === null) return 'null';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

// A well-formed from–to that no export covers (inverted, or longer than the
// exports allow) is still readable as typed; the pages name what is wrong.
function readableRange({ period, from, to }: PeriodQuery): CalendarPeriod | null {
  if (period || !from || !to || !isCalendarDate(from) || !isCalendarDate(to)) return null;
  return { kind: 'range', from, to };
}

// The object form (`?period={"kind":"quarter","quarter":"2026-Q2"}`): the
// router's own parsed state carried back through the URL middleware, and
// what older builds wrote into links. Read only when it is a period, and
// beside from/to only when they are that same range — the router's merge
// keeps a range link's own from/to next to it (Reconciliation swaps an
// inverted one, so either order).
function readPeriodObject(input: SearchParams, object: Record<string, unknown>): Period | null {
  const period = parsePeriod({ period: object });
  if (period.kind === 'current-month' && object.kind !== 'current-month') return null;
  const from = asQueryValue(input.from);
  const to = asQueryValue(input.to);
  if (from === null && to === null) return period;
  if (period.kind !== 'range') return null;
  const samePair = from === period.from && to === period.to;
  const swappedPair = from === period.to && to === period.from;
  return samePair || swappedPair ? period : null;
}

/**
 * Reads a link's period with the request grammar the CSV Edge Functions
 * and the accountant MCP use (parsePeriodQuery): `period`, or `from` with
 * `to`, never both. The raw params are checked before any fallback, so a
 * link that is malformed or mixed is refused, not resolved to whichever
 * half happens to parse.
 */
export function parseLinkPeriod(params: SearchParams): LinkPeriod {
  const input = withLinkPeriodParams(params);
  const periodObject = readObject(input, 'period');
  if (periodObject !== null) {
    const period = readPeriodObject(input, periodObject);
    if (period !== null) return { period, invalidPeriod: null };
    // Otherwise refused below, with the request parser's reason.
  }
  const query: PeriodQuery = {
    period: asQueryValue(input.period),
    from: asQueryValue(input.from),
    to: asQueryValue(input.to),
  };
  if (!query.period && !query.from && !query.to) {
    return { period: { kind: 'current-month' }, invalidPeriod: null };
  }
  try {
    return { period: parsePeriodQuery(query), invalidPeriod: null };
  } catch (err) {
    if (!(err instanceof PeriodError)) throw err;
    const range = readableRange(query);
    if (range) return { period: range, invalidPeriod: null };
    return {
      period: { kind: 'current-month' },
      invalidPeriod: { reason: err.message, query: periodParams(input) },
    };
  }
}

/** The URL form of a LinkPeriod: an unreadable link keeps its own params. */
export function serializeLinkPeriod({ period, invalidPeriod }: LinkPeriod): SearchParams {
  return invalidPeriod !== null && period.kind === 'current-month'
    ? { ...invalidPeriod.query }
    : serializePeriod(period);
}

export function parseAccountScope(params: SearchParams): AccountScope {
  const account = readString(params, 'account');
  if (account === 'all') return { kind: 'all' };
  if (account) return { kind: 'one', slug: account };

  // Object form (`?account={"kind":"one","slug":"x"}`) — round-trip from
  // TanStack Router's default JSON serializer.
  const accountObj = readObject(params, 'account');
  if (accountObj) {
    if (
      accountObj.kind === 'one' &&
      typeof accountObj.slug === 'string' &&
      accountObj.slug.trim().length > 0
    ) {
      return { kind: 'one', slug: accountObj.slug };
    }
    if (accountObj.kind === 'all') return { kind: 'all' };
  }

  return { kind: 'all' };
}

export function serializeAccountScope(value: AccountScope): SearchParams {
  if (value.kind === 'all') return {};
  return { account: value.slug };
}

export function parseSelected(params: SearchParams): string | null {
  return readString(params, 'selected') ?? null;
}

export function serializeSelected(id: string | null): SearchParams {
  return id === null ? {} : { selected: id };
}

export function parseFocus(params: SearchParams): string | null {
  return readString(params, 'focus') ?? null;
}

export function serializeFocus(id: string | null): SearchParams {
  return id === null ? {} : { focus: id };
}

export function parseNoAnim(params: SearchParams): boolean {
  const v = (params as Record<string, unknown>).noanim;
  // TanStack Router's default parser deserializes `noanim=1` as a number; the
  // raw-string path keeps tests/runtime aligned.
  return v === '1' || v === 1 || v === true;
}

export function serializeNoAnim(value: boolean): SearchParams {
  return value ? { noanim: '1' } : {};
}

function parseStatus(s: string | undefined): CategorizationStatusFilter {
  if (s === 'uncategorized' || s === 'auto' || s === 'agent_proposed' || s === 'manual') return s;
  return 'any';
}

function parseAmountFilter(s: string | undefined): AmountFilter {
  if (s === 'expenses' || s === 'negative' || s === 'positive') return s;
  return 'any';
}

function parseResolution(s: string | undefined): ResolutionFilter {
  if (s === undefined) return 'any';
  return (RESOLUTION_FILTERS as readonly string[]).includes(s) ? (s as ResolutionFilter) : 'any';
}

function parseSort(s: string | undefined): Sort {
  if (!s) return { column: 'booked_at', direction: 'desc' };
  const parts = s.split(':');
  // Strictly two segments. `booked_at:asc:extra` would otherwise parse as a
  // valid sort while silently dropping the trailing junk.
  if (parts.length !== 2) return { column: 'booked_at', direction: 'desc' };
  const [column, direction] = parts as [string, string];
  if (
    (SORT_COLUMNS as readonly string[]).includes(column) &&
    (direction === 'asc' || direction === 'desc')
  ) {
    return { column: column as SortColumn, direction };
  }
  return { column: 'booked_at', direction: 'desc' };
}

// Sort from `?sort=column:direction`, or the object form
// (`{"column":"amount_eur","direction":"asc"}`) the router's validated state
// carries back through the URL middleware.
function readSort(params: SearchParams): Sort {
  const sortObj = readObject(params, 'sort');
  if (sortObj) {
    return typeof sortObj.column === 'string' && typeof sortObj.direction === 'string'
      ? parseSort(`${sortObj.column}:${sortObj.direction}`)
      : parseSort(undefined);
  }
  return parseSort(readString(params, 'sort'));
}

function parseFiniteNumber(s: string | undefined): number | null {
  if (s === undefined) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export function parseTransactionsSearch(params: SearchParams): TransactionsSearch {
  return {
    ...parseLinkPeriod(params),
    account: parseAccountScope(params),
    status: parseStatus(readString(params, 'status')),
    categoryId: readString(params, 'category') ?? null,
    counterpartyId: readString(params, 'counterparty') ?? null,
    amountMin: parseFiniteNumber(readString(params, 'amount_min')),
    amountMax: parseFiniteNumber(readString(params, 'amount_max')),
    q: readString(params, 'q') ?? null,
    sort: readSort(params),
    selected: parseSelected(params),
    focus: parseFocus(params),
    noanim: parseNoAnim(params),
    amountFilter: parseAmountFilter(readString(params, 'amount_filter')),
    resolution: parseResolution(readString(params, 'resolution')),
  };
}

export function serializeTransactionsSearch(value: TransactionsSearch): SearchParams {
  const out: SearchParams = {
    ...serializeLinkPeriod(value),
    ...serializeAccountScope(value.account),
    ...serializeSelected(value.selected),
    ...serializeFocus(value.focus),
    ...serializeNoAnim(value.noanim),
  };
  if (value.status !== 'any') out.status = value.status;
  if (value.categoryId !== null) out.category = value.categoryId;
  if (value.counterpartyId !== null) out.counterparty = value.counterpartyId;
  if (value.amountMin !== null) out.amount_min = String(value.amountMin);
  if (value.amountMax !== null) out.amount_max = String(value.amountMax);
  if (value.q !== null) out.q = value.q;
  if (!(value.sort.column === 'booked_at' && value.sort.direction === 'desc')) {
    out.sort = `${value.sort.column}:${value.sort.direction}`;
  }
  if (value.amountFilter !== 'any') out.amount_filter = value.amountFilter;
  if (value.resolution !== 'any') out.resolution = value.resolution;
  return out;
}
